import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useCurrentUrl } from '@/hooks/useCurrentUrl';
import { useWidgetConfig } from '@/hooks/useWidgetConfig';
import type { PopupMessage } from '@/types/widgetConfig';

const ChatPromptWindow: React.FC = () => {
  const { welcomeMessage, popupMessages } = useWidgetConfig();
  const currentUrl = useCurrentUrl();

  // Helper function to check if a popup message matches current URL conditions
  const checkPopupMessageConditions = useCallback(
    (popupMessage: PopupMessage): boolean => {
      if (!popupMessage.published || !popupMessage.conditions.length) {
        return false;
      }

      const showConditions = popupMessage.conditions.filter(
        (condition) =>
          condition.rule === 'SHOW_WHEN_URL_CONTAINS' ||
          condition.rule === 'SHOW_WHEN_URL_IS',
      );

      const hideConditions = popupMessage.conditions.filter(
        (condition) =>
          condition.rule === 'HIDE_WHEN_URL_CONTAINS' ||
          condition.rule === 'HIDE_WHEN_URL_IS',
      );

      // If there are SHOW conditions, at least one must match
      let shouldShow = true;
      if (showConditions.length > 0) {
        shouldShow = showConditions.some((condition) => {
          const { rule, criteria } = condition;
          switch (rule) {
            case 'SHOW_WHEN_URL_CONTAINS':
              return currentUrl.includes(criteria);
            case 'SHOW_WHEN_URL_IS':
              return currentUrl === criteria;
            default:
              return false;
          }
        });
      }

      // If any HIDE condition matches, don't show the popup
      const shouldHide = hideConditions.some((condition) => {
        const { rule, criteria } = condition;
        switch (rule) {
          case 'HIDE_WHEN_URL_CONTAINS':
            return currentUrl.includes(criteria);
          case 'HIDE_WHEN_URL_IS':
            return currentUrl === criteria;
          default:
            return false;
        }
      });

      return shouldShow && !shouldHide;
    },
    [currentUrl],
  );

  const promptMessage = useMemo(() => {
    // First, check if there are any popup messages that match the current URL
    if (popupMessages && popupMessages.length > 0) {
      const matchingPopupMessage = popupMessages.find(
        checkPopupMessageConditions,
      );
      if (matchingPopupMessage) {
        return matchingPopupMessage.message;
      }
    }

    // If no popup message matches, show welcome message
    if (welcomeMessage) {
      return welcomeMessage;
    }

    // If no welcome message, show nothing
    return null;
  }, [popupMessages, welcomeMessage, checkPopupMessageConditions]);

  // Don't render anything if there's no message to show
  if (!promptMessage) {
    return null;
  }

  return (
    <div
      className={classNames(
        'sf:absolute sf:bottom-21 sf:right-4 sf:w-[260px] sf:transform sf:transition-all sf:duration-300 sf:ease-in-out sf:opacity-100 sf:translate-y-0 sf:scale-100',
        'sf:sm:bottom-17 sf:sm:right-0',
      )}
    >
      {/* Message bubble */}
      <div className="sf:bg-white sf:rounded-lg sf:px-3 sf:py-2 sf:shadow-md sf:relative sf:border sf:border-gray-30">
        {/* Message content */}
        <div className="sf:text-dark-blue-90 sf:text-sm sf:leading-relaxed">
          {promptMessage}
        </div>
      </div>
    </div>
  );
};

export default ChatPromptWindow;
